import { unstable_composeClasses as composeClasses } from '@mui/base';
import { ButtonBase, CircularProgress, styled, useThemeProps } from '@mui/material';
import clsx from 'clsx';
import { forwardRef } from 'react';
import splitButtonClasses, { getSplitButtonUtilityClass } from './splitButtonClasses';
import { capitalize } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getSplitButtonSlots = ownerState => {
  const {
    fullWidth,
    color,
    size,
    disabled,
    loading,
    edge
  } = ownerState;
  return {
    root: ['root', `color${capitalize(color)}`, `size${capitalize(size)}`, `edge${capitalize(edge)}`, fullWidth && 'fullWidth', disabled && 'disabled', loading && 'loading'],
    button: ['button'],
    content: ['content']
  };
};
const useUtilityClasses = ownerState => {
  const {
    classes
  } = ownerState;
  const slots = getSplitButtonSlots(ownerState);
  return composeClasses(slots, getSplitButtonUtilityClass, classes);
};
const SplitButtonRoot = styled('span', {
  name: 'MuiOriSplitButton',
  slot: 'Root',
  overridesResolver: ({
    ownerState
  }, styles) => [styles.root, styles[`color${capitalize(ownerState.color)}`], styles[`size${capitalize(ownerState.size)}`], styles[`edge${capitalize(ownerState.edge)}`], ownerState.disabled && styles.disabled, ownerState.loading && styles.loading, ownerState.fullWidth && styles.fullWidth]
})(({
  theme,
  ownerState
}) => ({
  display: 'inline-grid',
  gridTemplateAreas: '"content button"',
  border: '1px solid',
  borderRadius: '23px',
  height: '41px',
  gridTemplateColumns: `${ownerState.contentWidth} 1fr`,
  borderColor: theme.palette[ownerState.color].main,
  minWidth: 'min(320px, 100%)',
  overflow: 'hidden',
  ...(ownerState.size === 'small' && {
    height: '31px',
    borderRadius: '17.5px',
    minWidth: 'min(280px, 100%)'
  }),
  ...(ownerState.edge === 'start' && {
    gridTemplateAreas: '"button content"',
    gridTemplateColumns: `1fr ${ownerState.contentWidth}`
  }),
  ...(ownerState.fullWidth && {
    width: '100%'
  }),
  ...(ownerState.loading && {
    borderColor: theme.palette.action.disabledBackground
  }),
  [`&.${splitButtonClasses.disabled}`]: {
    borderColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    pointerEvents: 'none'
  }
}));
const Button = styled(ButtonBase, {
  name: 'MuiOriSplitButton',
  slot: 'Button',
  overridesResolver: (_, styles) => [styles.button]
})(({
  theme,
  ownerState
}) => ({
  backgroundColor: theme.palette[ownerState.color].main,
  color: theme.palette[ownerState.color].contrastText,
  gridArea: 'button',
  alignSelf: 'stretch',
  justifySelf: 'stretch',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  marginTop: '-2px',
  marginBottom: '-2px',
  '&:hover,&:focus': {
    backgroundColor: theme.palette[ownerState.color].dark,
    // Reset on touch devices, it doesn't add specificity
    '@media (hover: none)': {
      backgroundColor: theme.palette[ownerState.color].main
    }
  },
  ...(ownerState.size === 'small' && {
    ...(ownerState.edge === 'end' && {
      marginRight: -1
    }),
    ...(ownerState.edge === 'start' && {
      marginLeft: -1
    })
  }),
  ...(ownerState.edge === 'end' && {
    marginRight: -1
  }),
  ...(ownerState.edge === 'start' && {
    marginLeft: -1
  }),
  ...((ownerState.loading || ownerState.disabled) && {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled
  })
}));
const Content = styled('div', {
  name: 'MuiOriSplitButton',
  slot: 'Content',
  skipSx: true,
  overridesResolver: (_, styles) => [styles.content]
})(({
  ownerState
}) => ({
  gridArea: 'content',
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  ...(ownerState.edge === 'end' && {
    borderRadius: '23px 0 0 23px'
  }),
  ...(ownerState.edge === 'start' && {
    borderRadius: '0 23px 23px 0'
  }),
  ...(ownerState.size === 'small' && {
    ...(ownerState.edge === 'end' && {
      borderRadius: '13px 0 0 13px'
    }),
    ...(ownerState.edge === 'start' && {
      borderRadius: '0 13px 13px 0'
    })
  })
}));
const LoadingIndicator = /*#__PURE__*/_jsx(CircularProgress, {
  color: "inherit",
  size: 16
});
export const SplitButton = /*#__PURE__*/forwardRef(function SplitButton(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiOriSplitButton'
  });
  const {
    className,
    buttonContent,
    ButtonComponent = Button,
    ButtonProps,
    onClick,
    loadingIndicator = LoadingIndicator,
    disabled = false,
    loading = false,
    edge = 'end',
    color = 'primary',
    size = 'medium',
    contentWidth = 'auto',
    component = 'span',
    fullWidth,
    children,
    ...rest
  } = props;
  const ownerState = {
    ...props,
    edge,
    color,
    disabled,
    loading,
    size,
    contentWidth,
    fullWidth
  };
  const classes = useUtilityClasses(ownerState);
  return /*#__PURE__*/_jsxs(SplitButtonRoot, {
    ref: ref,
    as: component,
    ownerState: ownerState,
    className: clsx(classes.root, className),
    ...rest,
    children: [/*#__PURE__*/_jsx(Content, {
      ownerState: ownerState,
      className: classes.content,
      children: children
    }), /*#__PURE__*/_jsx(ButtonComponent, {
      disabled: disabled || loading,
      ownerState: ownerState,
      className: classes.button,
      onClick: onClick,
      ...ButtonProps,
      children: loading ? loadingIndicator : buttonContent
    })]
  });
});
export default SplitButton;